import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Drawer, Pagination, AgGridTable, Badge } from 'components/common';
import PropTypes from 'prop-types';
import { FiDownload, FiAward } from 'react-icons/fi';
import apiAxios from 'helpers/apiAxios';
import { downloadCsv, fetchAllDetailedRows } from 'helpers/exportCsv';
import { Link } from 'react-router-dom';

const fmtM = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
};

const fmt = n => Number(n || 0).toLocaleString('en-US');

const PAGE_SIZE = 30;

const DrillDownPanel = ({ open, title, baseParams, extraParams, onClose }) => {
  const [records,        setRecords]        = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [exporting,      setExporting]      = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [page,           setPage]           = useState(1);
  const [totalPages,     setTotalPages]     = useState(1);
  const [totalRecords,   setTotalRecords]   = useState(0);

  const fetchRecords = useCallback(async (pg) => {
    setLoading(true);
    try {
      const res = await apiAxios.get('/crm/dashboard/sales/get_detailed_data', {
        params: { ...baseParams, ...extraParams, pagination: { page: pg, per_page: PAGE_SIZE } }
      });
      setRecords(res.data?.data || []);
      setTotalPages(res.data?.pagination?.total_pages || 1);
      setTotalRecords(res.data?.pagination?.total_records || 0);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [baseParams, extraParams]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    fetchRecords(1);
  }, [open, fetchRecords]);

  const handlePage = (pg) => {
    setPage(pg);
    fetchRecords(pg);
  };

  const handleExport = async () => {
    setExporting(true);
    setExportProgress({ current: 0, total: 1 });
    try {
      const rows = await fetchAllDetailedRows(
        { ...baseParams, ...extraParams },
        200,
        (current, total) => setExportProgress({ current, total })
      );
      downloadCsv(rows, `${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`);
    } catch {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  };

  const columnDefs = useMemo(() => [
    {
      headerName: 'Q/J #',
      field:      'q_number',
      width:      140,
      pinned:     'left',
      cellRenderer: (p) => {
        const number = p.data?.j_number || p.data?.q_number || '—';
        const id     = p.data?.id;
        const isWon  = p.data?.sales_pipeline_group === 'Contract';
        return (
          <span className="tw-flex tw-items-center tw-gap-1">
            <Link to={`/crm/projects/${id}`} target="_blank" rel="noreferrer" className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline">
              {number}
            </Link>
            {p.data?.from_lead && (
              <span className="tw-text-xs tw-bg-violet-100 tw-text-violet-700 tw-px-1.5 tw-py-0.5 tw-rounded-xl tw-font-semibold">Lead</span>
            )}
            {isWon && <FiAward size={11} className="tw-text-yellow-500 tw-flex-shrink-0" title="Won" />}
          </span>
        );
      }
    },
    {
      headerName: 'Project',
      field:      'name',
      flex:       2,
      minWidth:   140,
      cellRenderer: (p) => (
        <span className="tw-truncate tw-block" title={p.value}><Link to={`/crm/projects/${p.data?.id}`} target="_blank" rel="noreferrer">{p.value || '—'}</Link></span>
      )
    },
    {
      headerName: 'Customer',
      field:      'company_name',
      flex:       1,
      minWidth:   110,
      cellRenderer: (p) => (
        <span className="tw-truncate tw-block tw-text-gray-600" title={p.value}>{p.value || '—'}</span>
      )
    },
    {
      headerName: 'Status',
      field:      'sales_pipeline_name',
      width:      160,
      cellRenderer: (p) => {
        if (!p.value) return '—';
        const variant = Badge.getVariantFromStatus(p.value);
        return (
          <Badge variant={variant} size="sm">
            {p.value}
          </Badge>
        );
      }
    },
    {
      headerName:     'Value',
      field:          'amount',
      width:          110,
      type:           'numericColumn',
      valueFormatter: p => p.value ? fmtM(p.value) : '—',
      cellStyle:      { textAlign: 'right', fontWeight: 600 }
    },
    {
      headerName:     'Weight (MT)',
      field:          'weight',
      width:          110,
      type:           'numericColumn',
      valueFormatter: p => p.value ? fmt(Math.round(p.value)) : '—',
      cellStyle:      { textAlign: 'right', color: '#0891b2' }
    }
  ], []);

  const drawerTitle = (
    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-pr-2">
      <div className="tw-flex tw-items-center tw-gap-2">
        <span className="tw-font-bold tw-text-slate-800">{title}</span>
        {!loading && totalRecords > 0 && (
          <span className="tw-text-xs tw-text-slate-500 tw-font-normal tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-xl">
            {fmt(totalRecords)} records
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={handleExport}
        disabled={exporting || loading || totalRecords === 0}
        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-border tw-border-emerald-300 tw-rounded tw-bg-emerald-50 tw-text-emerald-700 hover:tw-bg-emerald-100 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed tw-transition-colors tw-cursor-pointer"
        title="Export to CSV (opens in Excel)"
      >
        {exporting ? (
          <>
            <span className="tw-w-3 tw-h-3 tw-border-2 tw-border-emerald-400 tw-border-t-transparent tw-rounded-xl tw-animate-spin tw-inline-block" />
            {exportProgress && exportProgress.total > 1 && (
              <span>{exportProgress.current}/{exportProgress.total}</span>
            )}
          </>
        ) : (
          <FiDownload size={12} />
        )}
        {exporting ? 'Exporting…' : 'Export'}
      </button>
    </div>
  );

  const footer = totalPages > 1 ? (
    <div className="tw-w-full">
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalResults={totalRecords}
        perPage={PAGE_SIZE}
        onPageChange={handlePage}
        disabled={loading}
      />
    </div>
  ) : null;

  return (
    <Drawer
      show={open}
      onHide={onClose}
      title={drawerTitle}
      footer={footer}
      width="780px"
      minWidth="340px"
    >
      <AgGridTable
        rowData={records}
        columnDefs={columnDefs}
        loading={loading}
        pagination={false}
        filter={false}
        sort={false}
        rowHeight={36}
        headerHeight={38}
        fontSize="12px"
        height="max(320px, calc(100dvh - 180px))"
        enableCellTextSelection
      />
    </Drawer>
  );
};

DrillDownPanel.propTypes = {
  open:        PropTypes.bool.isRequired,
  title:       PropTypes.string.isRequired,
  baseParams:  PropTypes.object.isRequired,
  extraParams: PropTypes.object,
  onClose:     PropTypes.func.isRequired
};
DrillDownPanel.defaultProps = { extraParams: {} };

export default DrillDownPanel;
